import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { api, formatApiError, getUTM } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useFetch } from "@/lib/useFetch";
import { MkCard } from "@/components/mk/MkCard";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";

const GOALS = ["Increase Awareness", "Get PR Coverage", "Find Influencers", "Get Podcast Opportunities", "Attend Events", "Create Reels", "Launch Product", "Build Founder Brand", "Generate Business Leads", "Build Community"];

export default function BuildMyBrand() {
    const nav = useNavigate();
    const [goals, setGoals] = useState([]);
    const [form, setForm] = useState({});
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const { data: opps } = useFetch("/mk/opportunities", []);
    useSeo({ title: "Build My Brand | SuDigi", description: "Tell us your goals and get a personalised brand visibility plan." });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const toggle = (g) => setGoals((p) => p.includes(g) ? p.filter((x) => x !== g) : [...p, g]);

    const submit = async () => {
        if (!goals.length) { toast.error("Select at least one goal"); return; }
        if (!form.name || !form.mobile) { toast.error("Name and mobile are required"); return; }
        setLoading(true);
        try {
            await api.post("/leads", { name: form.name, mobile: form.mobile, email: form.email || undefined, business_name: form.business || "", city: form.city || "", business_category: form.industry || "", interested_service: goals.join(", "), budget: form.budget || "", message: `Brand visibility plan. Goals: ${goals.join(", ")}`, utm: { ...getUTM(), source: "build-my-brand" } });
            setDone(true);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
    };

    const reco = (opps || []).filter((o) => {
        if (goals.includes("Get Podcast Opportunities") && o.category === "PODCASTS") return true;
        if (goals.includes("Find Influencers") && o.category === "INFLUENCERS") return true;
        if (goals.includes("Attend Events") && ["EVENTS", "NETWORKING", "SEMINARS"].includes(o.category)) return true;
        if (goals.includes("Get PR Coverage") && o.category === "MEDIA") return true;
        return goals.length > 0;
    }).slice(0, 4);

    return (
        <section className="bg-teal-900 py-14 min-h-[70vh]">
            <div className="container-x max-w-3xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-brandborder bg-teal-700/40 px-3.5 py-1.5 text-xs font-semibold text-gold mb-4"><Sparkles className="w-3.5 h-3.5" /> Build My Brand</div>
                <h1 className="font-serif text-4xl text-cream">Let's build your brand visibility plan</h1>
                {!done ? (
                    <div className="mt-8 space-y-6">
                        <div>
                            <h3 className="text-cream font-semibold mb-3">What are you trying to achieve?</h3>
                            <div className="flex flex-wrap gap-2">
                                {GOALS.map((g) => (
                                    <button key={g} onClick={() => toggle(g)} className={`px-4 py-2 rounded-full text-sm transition-colors ${goals.includes(g) ? "bg-gold text-teal-900 font-semibold" : "border border-brandborder text-cream-soft hover:border-gold"}`} data-testid={`goal-${g.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                                        {goals.includes(g) && <Check className="w-3.5 h-3.5 inline mr-1" />}{g}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <In label="Name *" v={form.name} on={set("name")} t="bmb-name" />
                            <In label="Business Name" v={form.business} on={set("business")} t="bmb-business" />
                            <In label="Mobile *" v={form.mobile} on={set("mobile")} t="bmb-mobile" />
                            <In label="Email" v={form.email} on={set("email")} t="bmb-email" />
                            <In label="Industry" v={form.industry} on={set("industry")} t="bmb-industry" />
                            <In label="City" v={form.city} on={set("city")} t="bmb-city" />
                            <In label="Budget" v={form.budget} on={set("budget")} t="bmb-budget" />
                        </div>
                        <button onClick={submit} disabled={loading} className="btn-gold rounded-full px-8 py-3 flex items-center gap-2" data-testid="bmb-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Generate My Plan</button>
                    </div>
                ) : (
                    <div className="mt-8" data-testid="bmb-result">
                        <div className="card-teal rounded-2xl p-6 bg-gradient-to-br from-teal-700 to-teal-800 border border-gold/30">
                            <h2 className="font-serif text-2xl text-cream">Your Brand Visibility Plan is ready</h2>
                            <p className="text-cream-soft mt-2">Based on your goals ({goals.join(", ")}), here's where we'd start. A SuDigi expert will reach out to refine this with you.</p>
                        </div>
                        {reco.length > 0 && (
                            <>
                                <h3 className="text-cream font-semibold mt-8 mb-4">Recommended opportunities</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{reco.map((o) => <MkCard key={o.slug} item={o} kind="opportunities" />)}</div>
                            </>
                        )}
                        <button onClick={() => nav("/opportunities")} className="btn-ghost-cream rounded-full px-6 py-3 mt-8 flex items-center gap-2">Explore all opportunities <ArrowRight className="w-4 h-4" /></button>
                    </div>
                )}
            </div>
        </section>
    );
}
function In({ label, v, on, t }) { return <div className="space-y-1.5"><label className="text-cream-soft text-sm">{label}</label><Input className="bg-teal-800/50 border-brandborder text-cream" value={v || ""} onChange={on} data-testid={t} /></div>; }
